import React, { useState, useEffect } from 'react';
import axios from 'axios';
import './InvoiceManagement.css';
import { API_BASE_URL, getAuthHeaders } from '../../config/api.config';

interface Invoice {
  id: number;
  invoiceNumber: string;
  type: string;
  typeDisplayName: string;
  invoiceDate: string;
  dueDate?: string;
  totalAmount: number;
  amountDue: number;
  amountPaid: number;
  status: string;
  billingName?: string;  // camelCase from API
  BillingName?: string;  // PascalCase from API - fallback
  bookingId: number;
}

interface Booking {
  id: number;
  bookingNumber: string;
  customerName: string;
  companyName?: string;
  bookingDate: string;
  participantCount: number;
  variant: string;
  status: string;
  totalAmount: number;
  createdAt: string;
}

const InvoiceManagement: React.FC = () => {
  const [invoices, setInvoices] = useState<Invoice[]>([]);
  const [bookings, setBookings] = useState<Booking[]>([]);
  const [selectedInvoice, setSelectedInvoice] = useState<Invoice | null>(null);
  const [selectedBooking, setSelectedBooking] = useState<Booking | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [view, setView] = useState<'list' | 'generate'>('list');
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState<string>('all');
  const [bookingSearchTerm, setBookingSearchTerm] = useState('');
  const [bookingVariantFilter, setBookingVariantFilter] = useState<string>('all');

  const API_BASE = API_BASE_URL;

  useEffect(() => {
    loadInvoices();
    loadBookings();
  }, []);

  const loadInvoices = async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem('authToken');
      const response = await axios.get(`${API_BASE}/invoices`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setInvoices(response.data.invoices || []);
      setError(null);
    } catch (err: any) {
      setError(`Fehler beim Laden der Rechnungen: ${err.message}`);
    } finally {
      setLoading(false);
    }
  };

  const loadBookings = async () => {
    try {
      const token = localStorage.getItem('authToken');
      // Request all bookings with a large page size to avoid pagination issues
      const response = await axios.get(`${API_BASE}/bookings?pageSize=100`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      // API returns paginated response: { items: [...], pagination: {...} }
      const bookingsArray = response.data.items || response.data || [];
      // Filter for confirmed or fully paid bookings with valid prices
      const eligibleBookings = bookingsArray.filter((b: Booking) =>
        (b.status === 'Confirmed' || b.status === 'FullyPaid') && b.totalAmount > 0
      );
      setBookings(eligibleBookings);
    } catch (err: any) {
      console.error('Error loading bookings:', err);
    }
  };

  const generateInvoice = async (bookingId: number, type: 'anzahlung' | 'schlussrechnung' | 'kurzfristig') => {
    try {
      setLoading(true);
      const token = localStorage.getItem('authToken');
      const response = await axios.post(
        `${API_BASE}/invoices/booking/${bookingId}/${type}?sendEmail=false`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );

      if (response.data.success) {
        alert(`${type === 'anzahlung' ? 'Anzahlungsrechnung' : type === 'schlussrechnung' ? 'Schlussrechnung' : 'Rechnung'} erfolgreich erstellt!`);
        loadInvoices();
        setView('list');
      }
    } catch (err: any) {
      setError(`Fehler beim Erstellen der Rechnung: ${err.response?.data?.message || err.message}`);
    } finally {
      setLoading(false);
    }
  };

  const downloadPdf = async (invoiceId: number, invoiceNumber: string) => {
    try {
      const token = localStorage.getItem('authToken');
      const response = await axios.get(`${API_BASE}/invoices/${invoiceId}/pdf`, {
        headers: { Authorization: `Bearer ${token}` },
        responseType: 'blob'
      });

      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `${invoiceNumber}.pdf`);
      document.body.appendChild(link);
      link.click();
      link.remove();
    } catch (err: any) {
      setError(`Fehler beim Download: ${err.message}`);
    }
  };

  const sendInvoiceEmail = async (invoiceId: number) => {
    try {
      setLoading(true);
      const token = localStorage.getItem('authToken');
      await axios.post(
        `${API_BASE}/invoices/${invoiceId}/send`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      alert('Rechnung erfolgreich versendet!');
    } catch (err: any) {
      setError(`Fehler beim Versenden: ${err.message}`);
    } finally {
      setLoading(false);
    }
  };

  const getStatusBadge = (status: string) => {
    const statusMap: Record<string, string> = {
      'Draft': 'status-draft',
      'Sent': 'status-sent',
      'Paid': 'status-paid',
      'Overdue': 'status-overdue',
      'PartiallyPaid': 'status-partial',
      'Created': 'status-draft'
    };
    return statusMap[status] || 'status-default';
  };

  const getStatusLabel = (status: string) => {
    const statusLabels: Record<string, string> = {
      'Draft': 'Entwurf',
      'Created': 'Erstellt',
      'Sent': 'Versendet',
      'Paid': 'Bezahlt',
      'Overdue': 'Überfällig',
      'PartiallyPaid': 'Teilweise bezahlt'
    };
    return statusLabels[status] || status;
  };

  // Filter and search logic for invoices
  const filteredInvoices = invoices.filter(invoice => {
    const customerName = invoice.billingName || invoice.BillingName || '';
    const matchesSearch =
      invoice.invoiceNumber.toLowerCase().includes(searchTerm.toLowerCase()) ||
      customerName.toLowerCase().includes(searchTerm.toLowerCase()) ||
      invoice.typeDisplayName.toLowerCase().includes(searchTerm.toLowerCase());

    const matchesStatus = statusFilter === 'all' || invoice.status === statusFilter;

    return matchesSearch && matchesStatus;
  });

  // Filter and search logic for bookings
  const filteredBookings = bookings.filter(booking => {
    const matchesSearch =
      booking.bookingNumber.toLowerCase().includes(bookingSearchTerm.toLowerCase()) ||
      booking.customerName.toLowerCase().includes(bookingSearchTerm.toLowerCase()) ||
      (booking.companyName && booking.companyName.toLowerCase().includes(bookingSearchTerm.toLowerCase()));

    const matchesVariant = bookingVariantFilter === 'all' || booking.variant === bookingVariantFilter;

    return matchesSearch && matchesVariant;
  });

  if (loading && invoices.length === 0) {
    return <div className="loading">Lädt Rechnungen...</div>;
  }

  return (
    <div className="invoice-management">
      <div className="invoice-header">
        <h1>Rechnungsverwaltung</h1>
        <div className="header-actions">
          <button
            className={view === 'list' ? 'btn-primary active' : 'btn-secondary'}
            onClick={() => setView('list')}
          >
            Rechnungen ({invoices.length})
          </button>
          <button
            className={view === 'generate' ? 'btn-primary active' : 'btn-secondary'}
            onClick={() => setView('generate')}
          >
            Neue Rechnung erstellen
          </button>
        </div>
      </div>

      {error && (
        <div className="error-message">
          {error}
          <button onClick={() => setError(null)}>✕</button>
        </div>
      )}

      {view === 'list' ? (
        <div className="invoices-list">
          <div className="search-filter-bar">
            <input
              type="text"
              className="search-input"
              placeholder="Suche nach Rechnungsnr., Kunde oder Typ..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
            />
            <select
              className="status-filter"
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value)}
            >
              <option value="all">Alle Status</option>
              <option value="Draft">Entwurf</option>
              <option value="Sent">Versendet</option>
              <option value="Paid">Bezahlt</option>
              <option value="PartiallyPaid">Teilweise bezahlt</option>
              <option value="Overdue">Überfällig</option>
            </select>
            <div className="results-count">
              {filteredInvoices.length} von {invoices.length} Rechnungen
            </div>
          </div>

          <table className="invoices-table">
            <thead>
              <tr>
                <th>Rechnungsnr.</th>
                <th>Typ</th>
                <th>Kunde</th>
                <th>Datum</th>
                <th>Fällig</th>
                <th>Betrag</th>
                <th>Offen</th>
                <th>Status</th>
                <th>Aktionen</th>
              </tr>
            </thead>
            <tbody>
              {filteredInvoices.map(invoice => (
                <tr key={invoice.id} onClick={() => setSelectedInvoice(invoice)}>
                  <td className="invoice-number">{invoice.invoiceNumber}</td>
                  <td>{invoice.typeDisplayName}</td>
                  <td>{invoice.billingName || invoice.BillingName || <span className="text-muted">Kein Name</span>}</td>
                  <td>{new Date(invoice.invoiceDate).toLocaleDateString('de-DE', { day: '2-digit', month: '2-digit', year: 'numeric' })}</td>
                  <td>{invoice.dueDate ? new Date(invoice.dueDate).toLocaleDateString('de-DE', { day: '2-digit', month: '2-digit', year: 'numeric' }) : '-'}</td>
                  <td className="amount">{invoice.totalAmount.toLocaleString('de-DE', { minimumFractionDigits: 2, maximumFractionDigits: 2 })} €</td>
                  <td className="amount">{invoice.amountDue.toLocaleString('de-DE', { minimumFractionDigits: 2, maximumFractionDigits: 2 })} €</td>
                  <td>
                    <span className={`status-badge ${getStatusBadge(invoice.status)}`}>
                      {getStatusLabel(invoice.status)}
                    </span>
                  </td>
                  <td className="actions">
                    <button
                      className="btn-icon"
                      onClick={(e) => { e.stopPropagation(); downloadPdf(invoice.id, invoice.invoiceNumber); }}
                      title="PDF herunterladen"
                    >
                      📄
                    </button>
                    <button
                      className="btn-icon"
                      onClick={(e) => { e.stopPropagation(); sendInvoiceEmail(invoice.id); }}
                      title="E-Mail senden"
                    >
                      📧
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>

          {invoices.length === 0 && (
            <div className="empty-state">
              <p>Keine Rechnungen vorhanden</p>
              <button onClick={() => setView('generate')}>Erste Rechnung erstellen</button>
            </div>
          )}
        </div>
      ) : (
        <div className="generate-invoice">
          <h2>Neue Rechnung erstellen</h2>

          <div className="search-filter-bar">
            <input
              type="text"
              className="search-input"
              placeholder="Suche nach Buchungsnr., Kunde oder Firma..."
              value={bookingSearchTerm}
              onChange={(e) => setBookingSearchTerm(e.target.value)}
            />
            <select
              className="status-filter"
              value={bookingVariantFilter}
              onChange={(e) => setBookingVariantFilter(e.target.value)}
            >
              <option value="all">Alle Varianten</option>
              <option value="Standard">Standard</option>
              <option value="Premium">Premium</option>
            </select>
            <div className="results-count">
              {filteredBookings.length} von {bookings.length} Buchungen
            </div>
          </div>

          <div className="bookings-grid">
            {filteredBookings.map(booking => (
              <div key={booking.id} className="booking-card">
                <div className="booking-header">
                  <h3>{booking.customerName}</h3>
                  <span className="booking-id">#{booking.bookingNumber}</span>
                </div>
                <div className="booking-details">
                  <p><strong>Termin:</strong> {new Date(booking.bookingDate).toLocaleDateString('de-DE', { day: '2-digit', month: '2-digit', year: 'numeric' })}</p>
                  <p><strong>Teilnehmer:</strong> {booking.participantCount}</p>
                  <p><strong>Variante:</strong> {booking.variant}</p>
                  <p><strong>Gesamtpreis:</strong> {booking.totalAmount.toLocaleString('de-DE', { minimumFractionDigits: 2, maximumFractionDigits: 2 })} €</p>
                </div>
                <div className="booking-actions">
                  <button
                    className="btn-primary btn-sm"
                    onClick={() => generateInvoice(booking.id, 'anzahlung')}
                  >
                    Anzahlungsrechnung (20%)
                  </button>
                  <button
                    className="btn-primary btn-sm"
                    onClick={() => generateInvoice(booking.id, 'schlussrechnung')}
                  >
                    Schlussrechnung (80%)
                  </button>
                  <button
                    className="btn-primary btn-sm"
                    onClick={() => generateInvoice(booking.id, 'kurzfristig')}
                  >
                    Kurzfristige Rechnung (100%)
                  </button>
                </div>
              </div>
            ))}
          </div>

          {filteredBookings.length === 0 && bookings.length > 0 && (
            <div className="empty-state">
              <p>Keine Buchungen gefunden mit den aktuellen Filtern</p>
            </div>
          )}

          {bookings.length === 0 && (
            <div className="empty-state">
              <p>Keine Buchungen für Rechnungserstellung verfügbar</p>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default InvoiceManagement;
